/**
 * HomeScene — empty-document landing page redesign.
 *
 * Replaces the idle onboarding checklist for blank Word documents with a
 * hero card (greeting, static 3-step strip, "+ New document" CTA) and a
 * list of the user's drafted documents. Clicking a document triggers the
 * same load-into-Word flow as DraftsPanel's RunRow.handleLoad, so the
 * document content is hydrated and the shell exits idle to show the main UI.
 *
 * Spec: addin/src/variant-c/hooks/landingpage.txt
 * Data fetching mirrors DraftsPanel.tsx (listUserRuns, listUserActiveRuns,
 * 15s polling paused on document.hidden).
 */

import * as React from "react";
import { makeStyles } from "@fluentui/react-components";
import type { ActiveRunSummary, DocTypeConfig } from "../../api/client";
import {
  blobToBase64,
  fetchRunBundle,
  fetchRunDocument,
  getDocTypeConfig,
  listActiveRuns,
  listUserActiveRuns,
  listUserRuns,
  renameRun,
  runDocumentUrl,
} from "../../api/client";
import { openExternal } from "../services/openExternal";
import type { Workspace } from "../../types";
import type { DocSessionContext } from "../../services/wordWriter";
import {
  hydrateLiveDocFromBase64,
  saveDocSelectionProps,
  saveDocumentId,
  saveWordHydrationMarker,
  saveWordInsertionMode,
  setActiveRunId,
} from "../../services/wordWriter";
import { loadCustomRunIfApplicable } from "../services/loadCustomRunIntoLiveDoc";
import { loadRunIntoLiveDoc } from "../services/loadRunIntoLiveDoc";
import { rememberRunRecoveryHint } from "../services/documentSessionService";
import { buildGreeting, mapRunsToHomeDocs, type HomeDoc } from "../homeSceneMapping";
import { HomeDocCard } from "./HomeDocCard";
import { buildLoadSteps, HomeLoadProgress } from "./HomeLoadProgress";
import { HomeMenu } from "./HomeMenu";
import { HomePromptDialog, type HomePromptRequest } from "./HomePromptDialog";
import { useFolders } from "../hooks/useFiling";
import type { SpineSection } from "../hooks/useVariantCRun";
import { brandColors, inkColors, surfaceColors, fonts } from "../tokens";

/** The pseudo-folder id for "Unfiled" — documents with no ``folder_id``. Not a
 *  real folder: never renamed, deleted, or sent to the server. */
const UNFILED = "__unfiled__";

const POLL_INTERVAL_MS = 15_000;

interface OfficeLikeError {
  code?: string;
  message?: string;
  debugInfo?: {
    code?: string;
    message?: string;
    errorLocation?: string;
    statements?: string;
  };
}

function asOfficeError(err: unknown): OfficeLikeError | null {
  if (err && typeof err === "object" && ("debugInfo" in err || "code" in err)) {
    return err as OfficeLikeError;
  }
  return null;
}

function logLoadError(op: string, err: unknown): void {
  const oe = asOfficeError(err);
  if (oe) {
    console.error(`[HomeScene/Load] Office error in ${op}`, {
      code: oe.code,
      message: oe.message,
      errorLocation: oe.debugInfo?.errorLocation,
      debugInfo: oe.debugInfo,
    });
  } else {
    console.error(`[HomeScene/Load] error in ${op}`, err);
  }
}

function loadFailureMessage(err: unknown, prefix = "Load failed"): string {
  const oe = asOfficeError(err);
  const code = oe?.code ?? oe?.debugInfo?.code;
  if (code === "ItemNotFound" || code === "GeneralException") {
    return `${prefix}: couldn't rebuild this draft in the document. Try again.`;
  }
  const detail = err instanceof Error ? err.message : String(err);
  return `${prefix}: ${detail}`;
}

export interface RunLoadContext {
  libraryId: string | null;
  libraryName: string | null;
  workspaceId: string | null;
  docType: string;
  /** Submission mode of the loaded run. ``"custom"`` (build-on-existing-doc)
   *  with an empty ``libraryId`` means the writer intentionally skipped the
   *  source library — ``handleRunLoaded`` must treat that as "no library"
   *  rather than restoring a stale one from the Word custom props. */
  submissionMode?: string | null;
  /** Client-side custom spine built from the imported document's own heading
   *  outline. Used as the authoritative spine when ``submissionMode==="custom"``
   *  and the server snapshot doesn't come back tagged custom (cold start /
   *  dropped field / deploy lag) — so the spine renders the writer's headings
   *  (green where content exists) instead of falling back to the doctype
   *  template skeleton. Only ever set by the custom-import flow. */
  customSpineFallback?: SpineSection[];
}

interface HomeSceneProps {
  userIdentifier: string | null;
  docConfig?: DocTypeConfig | null;
  /** Drafting/context workspace — stamped onto NEW documents created via the
   *  "＋ New document" CTA. NOT used to scope the run list (see
   *  ``scopeWorkspaceId``); conflating the two would create new drafts in the
   *  wrong workspace when the writer is merely browsing another workspace. */
  workspaceId?: string | null;
  /** All workspaces the user can browse — populates the run-scope dropdown.
   *  Empty/undefined hides the dropdown. */
  workspaces?: Workspace[];
  /** Which workspace's runs to list. ``null`` = All workspaces (fetch omits
   *  ``workspace_id`` → every run the user owns). */
  scopeWorkspaceId?: string | null;
  /** The ``doc_id`` bound to the Word document currently open. When one of the
   *  listed runs targets it, that card is marked ``current`` (floated to the
   *  top as a highlighted "Reconnect" card). ``null`` when no doc is bound. */
  currentDocId?: string | null;
  /** Invoked when the writer changes the scope dropdown. ``null`` = All. */
  onScopeChange?: (workspaceId: string | null) => void;
  onBeforeLoad?: () => void;
  onRunLoaded?: (runId: string, config: DocTypeConfig, context?: RunLoadContext) => void | Promise<void>;
  /** Start the new-document flow. ``folderId`` (when the writer picked one in
   *  the creation dialog) is the folder the new document should be filed in;
   *  null/undefined = unfiled. */
  onNewDoc?: (folderId?: string | null) => void;
  refreshSignal?: number;
  /** Drafts-tab mode. When true, the scene lists EVERY completed run (the
   *  cross-run revision history) instead of one card per document, and each
   *  card gets a ⋯ overflow menu with "Open in Word" and "Download bundle".
   *  Off (the default) for the empty-doc Home, which stays Load-only. */
  enableRunActions?: boolean;
}

/** Trigger a browser download for a Blob (audit bundle .zip). Mirrors the
 *  helper the legacy DraftsPanel used. */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Give the download a tick to start before revoking — some browsers race
  // the revoke against the download initiation.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    backgroundColor: surfaceColors.paperDim,
    overflow: "auto",
    padding: "12px 10px 16px",
    gap: "10px",
  },
  heroCard: {
    backgroundColor: `linear-gradient(105deg, ${brandColors.tint}, ${brandColors.light})`,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    padding: "14px 14px 13px",
    display: "flex",
    flexDirection: "column",
  },
  greeting: {
    fontFamily: fonts.display,
    fontSize: "17px",
    fontWeight: 700,
    color: inkColors.ink,
    letterSpacing: "-0.2px",
    lineHeight: 1.2,
    margin: 0,
  },
  subtext: {
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink3,
    marginTop: "3px",
    lineHeight: 1.45,
  },
  stepStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: "5px",
    marginTop: "11px",
    alignItems: "center",
  },
  stepPill: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    backgroundColor: "#fff",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "14px",
    padding: "3px 9px 3px 4px",
  },
  stepBadge: {
    width: "15px",
    height: "15px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: fonts.body,
    fontSize: "8.5px",
    fontWeight: 700,
    color: "#fff",
  },
  stepLabel: {
    fontFamily: fonts.body,
    fontSize: "10px",
    fontWeight: 500,
    color: inkColors.ink2,
  },
  arrow: {
    color: inkColors.ink5,
    fontSize: "10px",
  },
  ctaButton: {
    width: "100%",
    height: "34px",
    backgroundColor: brandColors.primary,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontFamily: fonts.body,
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    cursor: "pointer",
    marginTop: "11px",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: brandColors.mid,
    },
    "&:active": {
      opacity: 0.9,
    },
  },
  sectionLabel: {
    fontFamily: fonts.body,
    fontSize: "10px",
    fontWeight: 700,
    color: inkColors.ink4,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    margin: "4px 2px 0",
  },
  docList: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  folderGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: "8px",
    padding: "2px 0",
  },
  folderBox: {
    borderRadius: "9px",
    border: `1px solid ${surfaceColors.line}`,
    background: "#fff",
    position: "relative",
    "&:hover": { border: `1px solid ${surfaceColors.lineStrong}`, background: surfaceColors.paper },
  },
  folderBoxUnfiled: {
    border: `1px dashed ${surfaceColors.line}`,
    background: "transparent",
  },
  // The full-bleed clickable card face. Transparent so the wrapper's border and
  // hover show through; the ⋯ menu (folderBoxMenuSlot) overlays its top-right.
  folderBoxFace: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    width: "100%",
    padding: "10px 11px",
    background: "transparent",
    border: "none",
    borderRadius: "9px",
    cursor: "pointer",
    textAlign: "left",
    color: "inherit",
  },
  folderBoxMenuSlot: {
    position: "absolute",
    top: "7px",
    right: "7px",
  },
  folderBoxHead: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    // Keep the name clear of the absolutely-positioned ⋯ menu.
    paddingRight: "22px",
  },
  folderBoxName: {
    fontFamily: fonts.body,
    fontSize: "12.5px",
    fontWeight: 600,
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  folderBoxMeta: {
    fontFamily: fonts.body,
    fontSize: "10.5px",
    color: inkColors.ink4,
  },
  folderBackRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  backLink: {
    fontFamily: fonts.body,
    fontSize: "11.5px",
    color: inkColors.ink3,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "2px 0",
    "&:hover": { color: inkColors.ink },
  },
  newFolderBtn: {
    fontFamily: fonts.body,
    fontSize: "14px",
    lineHeight: 1,
    color: inkColors.ink2,
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    padding: "2px 7px",
    cursor: "pointer",
    "&:hover": { background: surfaceColors.paperDim },
  },
  emptyState: {
    fontSize: "12px",
    color: inkColors.ink4,
    padding: "4px 16px 12px",
  },
  errorBanner: {
    margin: "8px 0",
    padding: "8px 12px",
    backgroundColor: "#fef2f2",
    color: "#991b1b",
    border: "1px solid #fecaca",
    borderRadius: "6px",
    fontSize: "12px",
  },
  loadingPlaceholder: {
    fontFamily: fonts.body,
    fontSize: "12px",
    color: inkColors.ink4,
    padding: "4px 16px 12px",
  },
  footerNote: {
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    textAlign: "center",
    marginTop: "4px",
    lineHeight: 1.5,
  },
});

export function HomeScene({
  userIdentifier,
  docConfig,
  workspaceId,
  workspaces = [],
  scopeWorkspaceId = null,
  currentDocId = null,
  onScopeChange,
  onBeforeLoad,
  onRunLoaded,
  onNewDoc,
  refreshSignal,
  enableRunActions = false,
}: HomeSceneProps): React.ReactElement {
  const styles = useStyles();
  const [homeDocs, setHomeDocs] = React.useState<HomeDoc[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // Which folder is open (null = the top-level folder grid + unfiled docs). A
  // folder id drills in and scopes the fetch to that folder's documents, so a
  // drilled-in view is not limited to the top-level recent window. Declared
  // before ``refresh`` so the fetch can read it.
  const [openFolder, setOpenFolder] = React.useState<string | null>(null);
  // Which run's overflow action is in flight, keyed by runId, so only the
  // acting card shows "Opening…" / "Zipping…".
  const [menuBusy, setMenuBusy] = React.useState<{ runId: string; action: "open" | "bundle" } | null>(null);
  // The draft currently being loaded into Word (null when idle). While
  // set, the doc list is replaced by <HomeLoadProgress>; ``loadStepIndex``
  // is the active setup step and ``loadFailed`` freezes the stepper on
  // the failed step so the writer can read the error and retry.
  const [loadingDoc, setLoadingDoc] = React.useState<HomeDoc | null>(null);
  const [loadStepIndex, setLoadStepIndex] = React.useState(-1);
  // Per-section fill progress for the "Rebuilding in Word" step — the
  // scaffold-then-fill Load path (loadRunIntoLiveDoc) emits one tick per
  // section so the card shows ``x / total`` advancing instead of freezing on
  // a large (e.g. 96-section) doc. Null while no fill is in flight.
  const [loadProgress, setLoadProgress] = React.useState<{ current: number; total: number } | null>(null);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const loadingDocId = loadingDoc?.runId ?? null;
  const isFetchingRef = React.useRef(false);

  const refresh = React.useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Completed runs are scoped to the selected workspace (``scopeWorkspaceId``;
      // null → all workspaces).
      //
      // Live runs come from TWO feeds, merged (deduped by run_id downstream):
      //   1. /runs/user/active — the writer's OWN in-progress runs, unscoped,
      //      so their actively-streaming draft is never hidden by the dropdown.
      //   2. /runs/active?workspace_id — every in-progress run in the selected
      //      workspace, CROSS-USER, so a teammate's live run in this workspace
      //      is attachable too. Only fetched when a workspace is selected
      //      (``/runs/active`` requires a scope; the "All workspaces" view has
      //      none to pass, so it falls back to the user feed alone).
      // When drilled into a folder, scope the completed feed to that folder so
      // its documents show even if they're outside the top-level recent window.
      // At the top level (null) fetch unscoped and group client-side. UNFILED
      // never reaches here — it isn't an ``openFolder`` value in this UI.
      const completedFolderScope = openFolder && openFolder !== UNFILED ? openFolder : null;
      const [completedResp, userActiveResp, wsActiveResp] = await Promise.all([
        listUserRuns(20, scopeWorkspaceId, completedFolderScope).catch((err: unknown) => {
          console.warn("[HomeScene] listUserRuns failed:", err);
          return { scope: "user" as const, count: 0, documents: [] };
        }),
        listUserActiveRuns(20).catch((err: unknown) => {
          console.warn("[HomeScene] listUserActiveRuns failed:", err);
          return { scope: "user" as const, count: 0, runs: [] as ActiveRunSummary[] };
        }),
        scopeWorkspaceId
          ? listActiveRuns({ workspaceId: scopeWorkspaceId, limit: 20 }).catch((err: unknown) => {
              console.warn("[HomeScene] listActiveRuns(workspace) failed:", err);
              return { scope: "workspace_id" as const, count: 0, runs: [] as ActiveRunSummary[] };
            })
          : Promise.resolve({ scope: "workspace_id" as const, count: 0, runs: [] as ActiveRunSummary[] }),
      ]);

      // Own runs first so a same-run_id collision keeps the user's own row.
      const mergedActive = [...(userActiveResp.runs ?? []), ...(wsActiveResp.runs ?? [])];

      const mapped = mapRunsToHomeDocs(
        completedResp.documents ?? [],
        mergedActive,
        currentDocId,
        userIdentifier,
        enableRunActions,
      );
      setHomeDocs(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setHomeDocs([]);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [scopeWorkspaceId, currentDocId, userIdentifier, enableRunActions, openFolder]);

  // --- Folders ------------------------------------------------------------
  // The workspaces whose folders this view lists: the scoped one, else every
  // workspace the writer can see, else the open document's workspace. A new
  // folder lands in the first of these.
  const folderWorkspaceIds = React.useMemo(() => {
    if (scopeWorkspaceId) return [scopeWorkspaceId];
    const ids = workspaces.map((w) => w.id).filter((id): id is string => Boolean(id));
    if (ids.length > 0) return ids;
    return workspaceId ? [workspaceId] : [];
  }, [scopeWorkspaceId, workspaces, workspaceId]);

  const {
    folders,
    reload: reloadFolders,
    create: createFolder,
    rename: renameFolder,
    remove: removeFolder,
    moveDocument,
  } = useFolders(folderWorkspaceIds);

  // A single name/choice dialog, with the action to run on its value.
  const [prompt, setPrompt] = React.useState<{
    request: HomePromptRequest;
    onValue: (value: string) => void | Promise<void>;
  } | null>(null);

  // Every document carries the folder it is filed in (``doc.folderId``). Group
  // the fetched cards by it so a folder box knows its count and the open view
  // shows only its documents.
  const docsByFolder = React.useMemo(() => {
    const map = new Map<string, HomeDoc[]>();
    for (const doc of homeDocs) {
      const key = doc.folderId ?? UNFILED;
      const list = map.get(key);
      if (list) list.push(doc);
      else map.set(key, [doc]);
    }
    return map;
  }, [homeDocs]);

  const visibleDocs = React.useMemo(() => {
    if (openFolder === null) return docsByFolder.get(UNFILED) ?? [];
    return docsByFolder.get(openFolder) ?? [];
  }, [openFolder, docsByFolder]);

  // A folder mutation or a move can change which folder a card belongs to, so
  // re-fetch the runs (folderId is joined server-side) as well as the folders.
  const refreshAll = React.useCallback(() => {
    void refresh();
    reloadFolders();
  }, [refresh, reloadFolders]);

  const askNewFolder = React.useCallback(
    (then?: (folderId: string) => void | Promise<void>) => {
      setPrompt({
        request: { kind: "name", title: "New folder", label: "Folder name", confirm: "Create" },
        onValue: async (name) => {
          const made = await createFolder(name);
          refreshAll();
          if (then) await then(made.id);
        },
      });
    },
    [createFolder, refreshAll],
  );

  const askRenameFolder = React.useCallback(
    (folderId: string, label: string) => {
      setPrompt({
        request: { kind: "name", title: "Rename folder", label: "Folder name", initial: label, confirm: "Save" },
        onValue: async (name) => {
          await renameFolder(folderId, name);
          refreshAll();
        },
      });
    },
    [renameFolder, refreshAll],
  );

  const handleDeleteFolder = React.useCallback(
    async (folderId: string) => {
      await removeFolder(folderId);
      setOpenFolder((open) => (open === folderId ? null : open));
      refreshAll();
    },
    [removeFolder, refreshAll],
  );

  // File one card into a folder — strictly per-document. Moving a card sets
  // ``folder_id`` on THAT document only (leaf or not); nothing else moves. A
  // leaf is never re-parented through its submission, which would sweep every
  // sibling doc and the live run into the folder.
  const fileCard = React.useCallback(
    async (doc: HomeDoc, folderId: string | null) => {
      await moveDocument(doc.docId, folderId);
      refreshAll();
    },
    [moveDocument, refreshAll],
  );

  const handleMoveToFolder = React.useCallback(
    (doc: HomeDoc) => {
      const options = [
        { value: UNFILED, label: "Unfiled" },
        ...(folders ?? []).map((f) => ({ value: f.id, label: f.label })),
        { value: "__new__", label: "New folder…" },
      ];
      setPrompt({
        request: { kind: "choice", title: "Move to folder", label: `Move “${doc.title}” to`, options },
        onValue: async (value) => {
          if (value === "__new__") {
            askNewFolder((folderId) => fileCard(doc, folderId));
            return;
          }
          await fileCard(doc, value === UNFILED ? null : value);
        },
      });
    },
    [folders, fileCard, askNewFolder],
  );

  const openFolderLabel = React.useMemo(() => {
    if (openFolder === null) return null;
    if (openFolder === UNFILED) return "Unfiled";
    return (folders ?? []).find((f) => f.id === openFolder)?.label ?? "Folder";
  }, [openFolder, folders]);

  // "＋ New document": offer a folder to file the new document in (existing,
  // Unfiled, or a brand-new one), then hand the chosen folder to the shell,
  // which threads it through to the run and files the doc once it exists. When
  // no workspace can hold a folder, start the flow unfiled with no dialog.
  const handleNewDoc = React.useCallback(() => {
    if (folderWorkspaceIds.length === 0) {
      onNewDoc?.();
      return;
    }
    const options = [
      { value: UNFILED, label: "Unfiled" },
      ...(folders ?? []).map((f) => ({ value: f.id, label: f.label })),
      { value: "__new__", label: "New folder…" },
    ];
    setPrompt({
      request: { kind: "choice", title: "New document", label: "File this document in", options },
      onValue: async (value) => {
        if (value === "__new__") {
          askNewFolder((folderId) => onNewDoc?.(folderId));
          return;
        }
        onNewDoc?.(value === UNFILED ? null : value);
      },
    });
  }, [folderWorkspaceIds, folders, onNewDoc, askNewFolder]);

  // Drafts-tab overflow actions. Open pops a fresh Word window on the run's
  // canonical .docx; Bundle downloads the audit .zip. Both are best-effort and
  // surface failures through the existing error banner.
  const handleOpenExternal = React.useCallback((doc: HomeDoc) => {
    try {
      openExternal(runDocumentUrl(doc.runId, false));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const handleDownloadBundle = React.useCallback(async (doc: HomeDoc) => {
    setMenuBusy({ runId: doc.runId, action: "bundle" });
    try {
      const blob = await fetchRunBundle(doc.runId, { includeProvenance: false });
      downloadBlob(blob, `run-${doc.runId}-bundle.zip`);
    } catch (err) {
      setError(`Bundle download failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setMenuBusy(null);
    }
  }, []);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  React.useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const refreshSignalSeenRef = React.useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshSignal is the external re-fetch trigger
  React.useEffect(() => {
    if (!refreshSignalSeenRef.current) {
      refreshSignalSeenRef.current = true;
      return;
    }
    void refresh();
  }, [refreshSignal, refresh]);

  // Dismiss the progress card after a failed load and return to the list.
  const handleDismissLoad = React.useCallback(() => {
    setLoadingDoc(null);
    setLoadStepIndex(-1);
    setLoadProgress(null);
    setLoadFailed(false);
    setError(null);
  }, []);

  const handleLoadDoc = React.useCallback(
    async (doc: HomeDoc) => {
      // Step 0 — "Preparing". Clearing loadFailed/error resets any prior
      // failed attempt so a retry starts from a clean stepper.
      setLoadingDoc(doc);
      setLoadStepIndex(0);
      setLoadProgress(null);
      setLoadFailed(false);
      setError(null);
      onBeforeLoad?.();

      try {
        // Resolve the config by the BACKEND doc_type (e.g.
        // "CMC_ANDA_SMALL_MOLECULE"), never the display code
        // (``doc.doctype`` = "CMC-ANDA") — the latter 404s
        // ``/doc-types/{id}``. Mirrors DraftsPanel's RunRow, which passes
        // ``run.doc_type``. When a config is already open we reuse it as-is.
        const cfg = docConfig ?? (await getDocTypeConfig(doc.backendDocType));

        // Steps 1→2 — prefer the canonical server-rendered .docx, whole-file.
        // fetchRunDocument (/runs/{id}/document) rebuilds the whole doc from the
        // Mongo SSOT with every section body already placed under its heading;
        // hydrateLiveDocFromBase64 inserts it in ~ONE Office.js operation, with
        // no add-in-side section-number matching. This is what the spine "Load
        // doc" (handleLoadDraftedDocument) and the legacy DraftsPanel already do,
        // and it is MUCH faster than the per-section replay: for a 91-section
        // protocol the scaffold-then-fill path does ~91 serialized Range.search +
        // insert + sync + citation round-trips, which dominate wall-clock on Word.
        //
        // The per-section scaffold-then-fill (loadRunIntoLiveDoc) is kept ONLY as
        // a fallback — for a 404 (no exported bundle yet, e.g. a still-running
        // run) and for a mid-insert whole-file failure (Word-for-the-web's
        // single-insert ceiling on very large docs, the reason this path was
        // originally per-section only). It reads /all-sections and wraps citations
        // per section, so the fallback body is byte-equivalent. Each emits a
        // progress tick to advance the card to "Rebuilding in Word (x/total)".
        const scaffoldFill = () =>
          loadRunIntoLiveDoc(doc.runId, cfg, {
            onProgress: ({ completed, total }) => {
              setLoadStepIndex(2);
              setLoadProgress({ current: completed, total });
            },
          });

        setLoadStepIndex(1); // Downloading your draft
        // Custom (build-on-existing-document) run → dedicated per-section Load;
        // the whole-file hydrate can't place its CUSTOM-N sections (blank doc)
        // and the template scaffoldFill would orphan them. Returns null for a
        // normal template run, which falls through to the hydrate path below.
        const customResult = await loadCustomRunIfApplicable(doc.runId, {
          onProgress: ({ completed, total }) => {
            setLoadStepIndex(2);
            setLoadProgress({ current: completed, total });
          },
        });
        if (!customResult) {
          const blob = await fetchRunDocument(doc.runId, { includeProvenance: false });
          if (blob) {
            const base64 = await blobToBase64(blob);
            try {
              setLoadStepIndex(2); // Rebuilding in Word (whole-file hydrate)
              await hydrateLiveDocFromBase64(base64, {
                styleSpec: cfg.live_doc_style_spec,
                skeleton: cfg.skeleton,
                headingNumbering: cfg.heading_numbering,
              });
            } catch (hydrateErr) {
              // The whole-file hydrate clears the body BEFORE inserting, so a
              // mid-insert failure leaves an EMPTY doc — the worst outcome. Rebuild
              // per-section from the backend SSOT so the writer still gets content.
              // Mirrors DraftsPanel.handleLoad / the spine's handleLoadDraftedDocument.
              logLoadError("handleLoadDoc.hydrate", hydrateErr);
              await scaffoldFill();
            }
          } else {
            // 404 — no exported bundle yet (still-running / paused run). Fall back
            // to per-section replay, which works from whatever /all-sections has.
            await scaffoldFill();
          }
        }

        // Step 3 — "Restoring session" (doc id, active run, markers, citations).
        setLoadStepIndex(3);
        try {
          if (doc.docId) {
            await saveDocumentId(doc.docId);
          }
          await setActiveRunId(doc.runId);
          await saveWordHydrationMarker({
            runId: doc.runId,
            snapshotVersion: null,
            snapshotTs: null,
          });
          await saveWordInsertionMode(cfg.generation?.insertion_mode === "section" ? "section" : "batch");

          if (doc.backendDocType && doc.backendDocType.length > 0) {
            // Persist the BACKEND doc_type (mirrors DraftsPanel) — the session
            // custom-props are read back to re-resolve the config, so storing
            // the display code "CMC-ANDA" would 404 on the next resolve.
            //
            // Use the run's REAL library id/name (carried on the HomeDoc from
            // the run list). Previously this stashed ``doc.docId`` as the
            // libraryId — a document id, not a library id — so the shell's
            // ``handleRunLoaded`` selection restore (which gates on
            // ``isValidLibraryId``) rejected it and both the doctype + library
            // chips stayed on their "pick a…" placeholders after Load.
            const sessionContext: DocSessionContext = {
              libraryId: doc.libraryId || "",
              libraryName: doc.libraryName || doc.libraryId || "",
              docCode: doc.backendDocType,
              docName: cfg.display_name || doc.backendDocType,
              backendDocType: doc.backendDocType,
              workspaceId: workspaceId ?? null,
            };
            await saveDocSelectionProps(sessionContext);
          }
        } catch (err) {
          logLoadError("postLoadBookkeeping", err);
        }

        const runLoadContext: RunLoadContext = {
          // The run's real library id/name — mirrors DraftsPanel.runLoadContext.
          // ``handleRunLoaded`` prefers this over the Word custom-prop round-trip
          // and only restores the doctype + library chips when the library id is
          // valid, so passing null here (the old behaviour) left them blank.
          libraryId: doc.libraryId,
          libraryName: doc.libraryName,
          workspaceId: workspaceId ?? null,
          // Backend doc_type (matches DraftsPanel) so ``handleRunLoaded`` restores
          // context against the real type, not the display code.
          docType: doc.backendDocType,
        };

        // Step 4 — "Loading the spine". When onRunLoaded resolves the shell
        // transitions out of idle and this scene unmounts into the ready
        // spine, so the stepper is torn down rather than showing a "done".
        setLoadStepIndex(4);
        await onRunLoaded?.(doc.runId, cfg, runLoadContext);
        // Success: clear the in-flight state. Usually a no-op because the
        // shell has already unmounted us, but harmless if it hasn't yet.
        setLoadingDoc(null);
        setLoadStepIndex(-1);
        setLoadProgress(null);
      } catch (err) {
        // Freeze the stepper on the failed step and surface the error in
        // the progress card (loadingDoc stays set → card stays mounted).
        logLoadError("handleLoadDoc", err);
        setError(loadFailureMessage(err));
        setLoadFailed(true);
      }
    },
    [docConfig, workspaceId, onRunLoaded, onBeforeLoad],
  );

  // Reconnect to an in-progress run. Unlike ``handleLoadDoc``, a live run has
  // no exported bundle yet, so we skip the whole-file download and paint the
  // sections drafted-so-far via the per-section path (``loadRunIntoLiveDoc``
  // reads DraftRunDoc.draft_full, written per-section as SSE fires). Once
  // ``onRunLoaded`` dispatches RUN_RESTORED, useVariantCRun re-subscribes to
  // /runs/{id}/events and streams the remaining sections into the spine live.
  // Mirrors DraftsPanel.handleJoinLive.
  const handleJoinLiveDoc = React.useCallback(
    async (doc: HomeDoc) => {
      // Step 0 "Preparing" → step 2 "Rebuilding in Word" (skip "Downloading",
      // there is no bundle to fetch for a live run).
      setLoadingDoc(doc);
      setLoadStepIndex(0);
      setLoadProgress(null);
      setLoadFailed(false);
      setError(null);
      onBeforeLoad?.();

      try {
        rememberRunRecoveryHint(doc.runId, "home.join-live");
        const cfg = docConfig ?? (await getDocTypeConfig(doc.backendDocType));

        setLoadStepIndex(2);
        await loadRunIntoLiveDoc(doc.runId, cfg, {
          onProgress: ({ completed, total }) => setLoadProgress({ current: completed, total }),
        });

        // Step 3 — "Restoring session". Best-effort: the sections are already
        // in the doc and SSE re-attach only needs onRunLoaded to fire, so a
        // custom-prop stamp failure must not abort the join.
        setLoadStepIndex(3);
        try {
          if (doc.docId) {
            await saveDocumentId(doc.docId);
          }
          await setActiveRunId(doc.runId);
          await saveWordHydrationMarker({ runId: doc.runId, snapshotVersion: null, snapshotTs: null });
          await saveWordInsertionMode(cfg.generation?.insertion_mode === "section" ? "section" : "batch");
          if (doc.backendDocType && doc.backendDocType.length > 0) {
            const sessionContext: DocSessionContext = {
              libraryId: doc.docId || "",
              libraryName: doc.docId || "",
              docCode: doc.backendDocType,
              docName: cfg.display_name || doc.backendDocType,
              backendDocType: doc.backendDocType,
              workspaceId: workspaceId ?? null,
            };
            await saveDocSelectionProps(sessionContext);
          }
        } catch (err) {
          logLoadError("handleJoinLiveDoc.postLoad", err);
        }

        const runLoadContext: RunLoadContext = {
          libraryId: null,
          libraryName: null,
          workspaceId: workspaceId ?? null,
          docType: doc.backendDocType,
        };

        // Step 4 — "Loading the spine". onRunLoaded dispatches RUN_RESTORED;
        // because the run is non-terminal the shell leaves terminalRunStatus
        // false and the SSE subscription re-opens, so the spine keeps filling.
        setLoadStepIndex(4);
        await onRunLoaded?.(doc.runId, cfg, runLoadContext);
        setLoadingDoc(null);
        setLoadStepIndex(-1);
        setLoadProgress(null);
      } catch (err) {
        logLoadError("handleJoinLiveDoc", err);
        setError(loadFailureMessage(err, "Reconnect failed"));
        setLoadFailed(true);
      }
    },
    [docConfig, workspaceId, onRunLoaded, onBeforeLoad],
  );

  // Route each card to the correct handler: a live run reconnects (join-live),
  // a completed run loads its exported bundle.
  const handleCardOpen = React.useCallback(
    (doc: HomeDoc) => (doc.live ? handleJoinLiveDoc(doc) : handleLoadDoc(doc)),
    [handleJoinLiveDoc, handleLoadDoc],
  );

  const handleRename = React.useCallback(
    async (doc: HomeDoc, name: string) => {
      try {
        await renameRun(doc.runId, name);
        await refresh();
      } catch (err) {
        console.warn("[HomeScene] renameRun failed:", err);
      }
    },
    [refresh],
  );

  const now = new Date();
  const greeting = buildGreeting(now, userIdentifier);

  return (
    <div className={styles.container}>
      <div className={styles.heroCard}>
        <h1 className={styles.greeting}>{greeting}</h1>
        <p className={styles.subtext}>Start a draft — a cited first draft, QC report included, lands in 1–2 hours.</p>

        <div className={styles.stepStrip}>
          <div className={styles.stepPill}>
            <div className={styles.stepBadge} style={{ backgroundColor: brandColors.primary }}>
              1
            </div>
            <span className={styles.stepLabel}>Choose doctype</span>
          </div>
          <span className={styles.arrow}>→</span>
          <div className={styles.stepPill}>
            <div className={styles.stepBadge} style={{ backgroundColor: brandColors.mid }}>
              2
            </div>
            <span className={styles.stepLabel}>Attach sources</span>
          </div>
          <span className={styles.arrow}>→</span>
          <div className={styles.stepPill}>
            <div className={styles.stepBadge} style={{ backgroundColor: "#26DF9B" }}>
              3
            </div>
            <span className={styles.stepLabel} style={{ color: inkColors.ink }}>
              Steer & run
            </span>
          </div>
        </div>

        <button className={styles.ctaButton} onClick={handleNewDoc} type="button">
          ＋ New document
        </button>
      </div>

      {loadingDoc ? (
        // A draft is being opened — replace the list with a step-by-step
        // progress card, right up until the shell swaps us for the spine.
        <HomeLoadProgress
          docTitle={loadingDoc.title}
          steps={buildLoadSteps(loadStepIndex, loadProgress)}
          error={loadFailed ? error : null}
          onDismiss={loadFailed ? handleDismissLoad : undefined}
        />
      ) : (
        <>
          {error && (
            <div className={styles.errorBanner} role="alert">
              Couldn&apos;t load your documents: {error}
            </div>
          )}

          {/* Top level: a "Folders" header (title + create button) over the
              folder grid, then a "Documents" header over the unfiled docs. */}
          {openFolder === null && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <div className={styles.sectionLabel}>Folders</div>
                {folderWorkspaceIds.length > 0 && (
                  <button
                    type="button"
                    className={styles.newFolderBtn}
                    onClick={() => askNewFolder()}
                    data-testid="home-new-folder"
                    aria-label="New folder"
                    title="New folder"
                  >
                    ＋
                  </button>
                )}
              </div>
              {workspaces.length > 0 && (
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "11px",
                    color: inkColors.ink3,
                  }}
                >
                  Workspace
                  <select
                    aria-label="Filter documents by workspace"
                    data-testid="home-workspace-scope"
                    value={scopeWorkspaceId ?? ""}
                    onChange={(e) => onScopeChange?.(e.target.value || null)}
                    style={{
                      border: `1px solid ${surfaceColors.line}`,
                      borderRadius: "6px",
                      padding: "3px 6px",
                      fontSize: "11px",
                      color: inkColors.ink,
                      background: surfaceColors.paper,
                      maxWidth: "180px",
                    }}
                  >
                    <option value="">All workspaces</option>
                    {workspaces.map((ws) => (
                      <option key={ws.id} value={ws.id}>
                        {ws.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {isLoading && homeDocs.length === 0 && (
            <div className={styles.loadingPlaceholder}>Loading your documents…</div>
          )}

          {/* Inside a folder: a back link, then that folder's documents. */}
          {openFolder === null && (folders?.length ?? 0) > 0 && (
            <div className={styles.folderGrid} data-testid="home-folder-grid">
              {(folders ?? []).map((folder) => (
                // The clickable card face is a real <button> (native keyboard/role);
                // the folder's ⋯ menu is an absolutely-positioned sibling, not a
                // child, so we never nest a button inside a button.
                <div key={folder.id} className={styles.folderBox}>
                  <button
                    type="button"
                    className={styles.folderBoxFace}
                    onClick={() => setOpenFolder(folder.id)}
                    data-testid="home-folder-box"
                  >
                    <div className={styles.folderBoxHead}>
                      <span className={styles.folderBoxName}>📁 {folder.label}</span>
                    </div>
                    <span className={styles.folderBoxMeta}>
                      {(docsByFolder.get(folder.id) ?? []).length} document
                      {(docsByFolder.get(folder.id) ?? []).length === 1 ? "" : "s"}
                    </span>
                  </button>
                  <div className={styles.folderBoxMenuSlot}>
                    <HomeMenu
                      label={`Folder actions for ${folder.label}`}
                      items={[
                        { label: "Rename", onSelect: () => askRenameFolder(folder.id, folder.label) },
                        { label: "Delete", destructive: true, onSelect: () => void handleDeleteFolder(folder.id) },
                      ]}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {openFolder !== null && (
            <div className={styles.folderBackRow}>
              <button type="button" className={styles.backLink} onClick={() => setOpenFolder(null)}>
                ← All folders
              </button>
              <div className={styles.sectionLabel}>{openFolderLabel}</div>
            </div>
          )}

          {openFolder === null && <div className={styles.sectionLabel}>Documents</div>}

          {visibleDocs.length === 0 && !isLoading && (
            <div className={styles.emptyState}>
              {openFolder === null
                ? "No documents yet — start your first draft above, or open a folder."
                : "This folder has no documents yet."}
            </div>
          )}

          <div className={styles.docList}>
            {visibleDocs.map((doc) => (
              <HomeDocCard
                key={doc.runId}
                doc={doc}
                onLoad={handleCardOpen}
                loading={loadingDocId === doc.runId}
                onOpenExternal={enableRunActions ? handleOpenExternal : undefined}
                onDownloadBundle={enableRunActions ? handleDownloadBundle : undefined}
                menuBusy={menuBusy?.runId === doc.runId ? menuBusy.action : null}
                onRename={handleRename}
                onMoveToFolder={folderWorkspaceIds.length > 0 ? handleMoveToFolder : undefined}
              />
            ))}
          </div>

          <HomePromptDialog
            request={prompt?.request ?? null}
            onSubmit={(value) => {
              const pending = prompt;
              setPrompt(null);
              void pending?.onValue(value);
            }}
            onCancel={() => setPrompt(null)}
          />
        </>
      )}

      <div className={styles.footerNote}>This pane works on the Word file that&apos;s open.</div>
    </div>
  );
}
